import { z } from 'zod';

import type { ActorRef, AuctionEventTypeValue } from './messaging.ts';

/**
 * Il registro dell'asta: ogni cosa accaduta, nell'ordine in cui è accaduta.
 *
 * È la stessa tabella di audit che il processor scrive nella transazione di
 * ciò che descrive — qui viene solo letta. Serve a due domande diverse:
 * «cos'è successo?», e «riportami a prima di questo». La seconda è il motivo
 * per cui l'ancora del rollback è una **riga di registro** e non una posizione
 * della sequenza: solo il registro distingue due passaggi sullo stesso
 * calciatore, e solo il registro sa cosa è venuto prima e cosa dopo.
 *
 * È materiale di conduzione, come la sequenza e lo storico delle offerte: la
 * rotta vive nello scope amministratore e nessun payload destinato ai
 * fantallenatori la contiene.
 */
export const AUCTION_LOG_ROUTES = {
  /** Template Fastify; per un URL concreto usa `auctionLogPath()`. */
  collection: '/auctions/:id/log',
} as const;

export function auctionLogPath(auctionId: string): string {
  return `/auctions/${encodeURIComponent(auctionId)}/log`;
}

/**
 * Quante righe al massimo torna una lettura.
 *
 * Un'asta lunga produce decine di migliaia di eventi e il registro si guarda
 * dal vivo, mentre si conduce: spedirli tutti riempirebbe di JSON una pagina
 * che ne mostra venti. Si prendono le **ultime**, che sono quelle che servono,
 * e la risposta dichiara se ne ha lasciate indietro.
 */
export const AUCTION_LOG_MAX_ENTRIES = 500;

export const auctionLogQuerySchema = z.object({
  /** Quante righe (le più recenti). Le query string arrivano come stringhe. */
  limit: z.coerce.number().int().min(1).max(AUCTION_LOG_MAX_ENTRIES).optional(),
});

export type AuctionLogQuery = z.output<typeof auctionLogQuerySchema>;

/**
 * Una riga del registro.
 *
 * Porta identificativi, non nomi: il nome di un calciatore o di un
 * fantallenatore il client ce l'ha già nello snapshot, e duplicarlo qui
 * significherebbe spedire due volte la stessa asta. `payload` è il contenuto
 * specifico dell'evento, così com'è stato scritto.
 */
export interface AuctionLogEntry {
  readonly eventId: string;
  /**
   * Ordine totale per asta, assegnato dal database.
   *
   * Serve perché il tempo non basta: gli eventi di una stessa transazione
   * condividono `occurredAt` al millisecondo (in PostgreSQL `now()` è l'istante
   * di **inizio transazione**), e un'aggiudicazione con la chiamata successiva
   * nascono insieme. Questo numero è ciò che li tiene in fila.
   */
  readonly sequence: number;
  readonly type: AuctionEventTypeValue;
  /** ISO-8601 con i millisecondi: l'orologio del database. */
  readonly occurredAt: string;
  readonly actor: ActorRef;
  /** Comando che ha prodotto la riga, `null` per gli eventi spontanei del motore. */
  readonly commandId: string | null;
  readonly playerId: string | null;
  readonly participantId: string | null;
  readonly payload: Readonly<Record<string, unknown>>;
}

export interface AuctionLogResponse {
  /** Dalla più vecchia alla più recente. */
  readonly entries: readonly AuctionLogEntry[];
  /** Quante righe ha in tutto l'asta: `entries.length` minore significa troncato. */
  readonly total: number;
}
